import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type Theme = 'dark' | 'light';
type ThemeMode = 'dark' | 'light' | 'system';

interface ThemeContextValue {
	theme: Theme;
	themeMode: ThemeMode;
	setThemeMode: (mode: ThemeMode) => void;
	toggleTheme: () => void;
}

const STORAGE_KEY = 'ielect-theme';

const getSystemTheme = (): Theme => (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

const resolveTheme = (mode: ThemeMode): Theme => (mode === 'system' ? getSystemTheme() : mode);

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
	const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
		} catch {
			// localStorage unavailable
		}
		return 'system';
	});

	const [theme, setTheme] = useState<Theme>(() => resolveTheme(themeMode));

	const applyTheme = useCallback((t: Theme) => {
		document.documentElement.setAttribute('data-theme', t);
		setTheme(t);
	}, []);

	const setThemeMode = useCallback(
		(mode: ThemeMode) => {
			setThemeModeState(mode);
			try {
				localStorage.setItem(STORAGE_KEY, mode);
			} catch {
				// localStorage unavailable
			}
			applyTheme(resolveTheme(mode));
		},
		[applyTheme]
	);

	const toggleTheme = useCallback(() => {
		setThemeMode(theme === 'dark' ? 'light' : 'dark');
	}, [theme, setThemeMode]);

	// Listen for OS preference changes when in system mode
	useEffect(() => {
		if (themeMode !== 'system') return;

		const mql = window.matchMedia('(prefers-color-scheme: dark)');
		const handler = (e: MediaQueryListEvent) => {
			applyTheme(e.matches ? 'dark' : 'light');
		};
		mql.addEventListener('change', handler);
		return () => mql.removeEventListener('change', handler);
	}, [themeMode, applyTheme]);

	// Apply theme on mount
	useEffect(() => {
		applyTheme(resolveTheme(themeMode));
	}, []);

	const value = useMemo(() => ({ theme, themeMode, setThemeMode, toggleTheme }), [theme, themeMode, setThemeMode, toggleTheme]);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
	return ctx;
};
